import React, { useState } from "react";
import * as m from "../../lib/model";
import { Card, MoneyInput } from "../shared";
import { debtApi } from "../../lib/api";
import { csvCell } from "../../lib/utils";
import { toast } from "sonner";
import { FileSpreadsheet, Pencil, Trash2, Plus } from "lucide-react";
import { Attachments } from "./Attachments";
import { annualDS } from "./DebtCompare";
import { DEBT_STAGES, LENDER_TYPES, RECOURSE, FACILITIES, YESNO } from "./DebtModal";

const MIN_COLS = 3;
const ROWS = [
  ["Lender & banker", null],
  ["Company name", "lender", "text"],
  ["Banker / contact", "contact", "text"],
  ["Banker title", "bankerTitle", "text"],
  ["Email", "email", "text"],
  ["Phone", "phone", "text"],
  ["Lender type", "type", "select", LENDER_TYPES],
  ["Stage", "stage", "select", DEBT_STAGES],
  ["Facility & pricing", null],
  ["Facility", "facility", "select", FACILITIES],
  ["Debt size ($)", "amount", "money"],
  ["Leverage (× EBITDA)", "_lev", "calc"],
  ["All-in rate (%)", "rate", "number"],
  ["Rate type", "rateType", "select", ["Fixed", "Floating (Prime +)", "Floating (SOFR +)"]],
  ["Index", "indexRate", "text"],
  ["Spread (%)", "spread", "number"],
  ["Term (yrs)", "termYears", "number"],
  ["Amortization (yrs)", "amortYears", "number"],
  ["Annual debt service", "_ds", "calc"],
  ["DSCR (base case)", "_dscr", "calc"],
  ["Fees (%)", "feesPct", "number"],
  ["Structure & protections", null],
  ["Recourse", "recourse", "select", RECOURSE],
  ["Personal guarantee", "personalGuarantee", "select", YESNO],
  ["Collateral required", "collateral", "select", YESNO],
  ["Min DSCR covenant (×)", "dscrMin", "number"],
  ["Max leverage covenant (×)", "maxLeverage", "number"],
  ["Prepayment penalty", "prepay", "select", YESNO],
  ["Deposit / treasury req.", "depositReq", "select", YESNO],
  ["Other covenants", "covenants", "text"],
  ["Process", null],
  ["Closing (days)", "closingDays", "number"],
  ["Term sheet received", "termReceived", "date"],
  ["Term sheet expires", "termExpires", "date"],
  ["Next follow-up", "nextFollowUp", "date"],
  ["Term sheet file", "_files", "files"],
  ["Notes", "notes", "text"],
];
const CSV_COLS = ROWS.filter((r) => r[1] && !r[1].startsWith("_")).map((r) => r[1]);
const DRAFT = { type: "Bank", stage: "Contacted", facility: "Senior term loan", rateType: "Fixed", recourse: "Full recourse", termYears: 10, amortYears: 10, closingDays: 60 };

const Cell = ({ row, lead, onCommit, disabled }) => {
  const [, key, type, opts] = row;
  const [v, setV] = useState(lead[key] ?? "");
  React.useEffect(() => { setV(lead[key] ?? ""); }, [lead, key]);
  const num = type === "number" || type === "money";
  const commit = () => { const val = num ? (v === "" ? 0 : +v) : v; if (val !== (lead[key] ?? (num ? 0 : ""))) onCommit(key, val); };
  const tid = `tsg-${key}-${lead.id || "new"}`;
  if (type === "money") return <MoneyInput value={v} onChange={setV} onBlur={commit} disabled={disabled} testid={tid} className="tsg-money" />;
  if (type === "select") return <select className="tsg-inp" value={lead[key] ?? opts[0]} disabled={disabled} onChange={(e) => onCommit(key, e.target.value)} data-testid={tid}>{opts.map((o) => <option key={o || "none"} value={o}>{o || "—"}</option>)}</select>;
  return <input className="tsg-inp" type={type === "number" ? "number" : type} step={type === "number" ? "any" : undefined} value={v} disabled={disabled}
    placeholder={disabled ? "" : key === "lender" ? "Type a company name…" : "—"} onChange={(e) => setV(e.target.value)} onBlur={commit} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} data-testid={tid} />;
};

export const TermSheets = ({ rows, ebitda, cfads, onEdit, onChange, onAdd, onCreate, onRemove }) => {
  const live = rows.filter((l) => l.stage !== "Passed");
  const [drafts, setDrafts] = useState([{ ...DRAFT, _k: 1 }]);
  const cols = [...live, ...drafts];
  while (cols.length < MIN_COLS) { const k = drafts.length + cols.length; drafts.push({ ...DRAFT, _k: k }); cols.push(drafts[drafts.length - 1]); }
  const today = new Date().toISOString().slice(0, 10);

  const calc = (l, key) => {
    if (key === "_lev") return ebitda > 0 && l.amount > 0 ? `${(l.amount / ebitda).toFixed(2)}×` : "—";
    const ds = annualDS(l);
    if (key === "_ds") return ds ? m.fmt$(ds) : "—";
    if (key === "_dscr") return ds && cfads > 0 ? m.fmtX(cfads / ds) : "—";
    return "—";
  };
  const patchLead = async (l, key, val) => {
    try { onChange(await debtApi.patch(l.id, { [key]: val })); } catch { toast.error("Could not save"); }
  };
  const draftSet = async (d, key, val) => {
    const nd = { ...d, [key]: val };
    setDrafts((ds) => ds.map((x) => (x._k === d._k ? nd : x)));
    if (key === "lender" && String(val).trim()) {
      const { _k, ...payload } = nd;
      try { await onCreate(payload); setDrafts((ds) => ds.filter((x) => x._k !== _k).concat(ds.length <= 1 ? [{ ...DRAFT, _k: Date.now() }] : [])); toast.success(`${val} added — fill in the rest of the column`); }
      catch { toast.error("Could not add lender"); }
    }
  };
  const exportCsv = () => {
    const header = ["Term", ...live.map((l, i) => `Lender ${i + 1}`)];
    const lines = ROWS.filter((r) => r[1] && !r[1].startsWith("_")).map((r) => [r[0], ...live.map((l) => csvCell(l[r[1]]))].join(","));
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([[header.join(","), ...lines].join("\n")], { type: "text/csv" }));
    a.download = "term-sheets.csv"; a.click();
  };

  return (
    <Card title={`Term sheet template — ${live.length} lender${live.length === 1 ? "" : "s"} · type straight into the grid`} testid="term-sheets"
      right={<div style={{ display: "flex", gap: 6 }}><button className="btn btn-sm" onClick={exportCsv} data-testid="ts-export"><FileSpreadsheet size={12} />CSV</button><button className="btn btn-sm" onClick={() => setDrafts((d) => [...d, { ...DRAFT, _k: Date.now() }])} data-testid="ts-add-col"><Plus size={12} />Column</button><button className="btn btn-primary btn-sm" onClick={onAdd} data-testid="ts-add">+ Add lender (form)</button></div>}>
      <div className="ts-wrap">
        <table className="tbl tsg" data-testid="ts-table">
          <thead>
            <tr>
              <th className="tsg-sticky tsg-head">Term</th>
              {cols.map((l, i) => (
                <th key={l.id || `d${l._k}`} className={`tsg-col ${l.id ? "" : "draft"} ${["Term Sheet", "Negotiating", "Committed"].includes(l.stage) ? "live" : ""}`} data-testid={l.id ? `ts-col-${l.id}` : `ts-col-new-${i}`}>
                  <div className="tsg-colhead">
                    <span className="tsg-n">Lender {i + 1}</span>
                    <b>{l.lender || <i>empty — start typing below</i>}</b>
                    {l.id && (
                      <span className="tsg-acts">
                        <button className="btn" onClick={() => onEdit(l)} title="Open form" data-testid={`ts-edit-${l.id}`}><Pencil size={11} /></button>
                        <button className="btn" onClick={() => onRemove(l)} title="Remove lender" data-testid={`ts-remove-${l.id}`}><Trash2 size={11} /></button>
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROWS.map((r) => r[1] === null ? (
              <tr key={r[0]} className="tsg-sec"><td className="tsg-sticky" colSpan={cols.length + 1}>{r[0]}</td></tr>
            ) : (
              <tr key={r[1]} data-testid={`tsg-row-${r[1]}`}>
                <td className="tsg-sticky tsg-lbl">{r[0]}</td>
                {cols.map((l, i) => {
                  const key = r[1], isDraft = !l.id;
                  if (r[2] === "calc") return <td key={i} className="tsg-calc">{isDraft ? "—" : calc(l, key)}</td>;
                  if (r[2] === "files") return <td key={i}>{isDraft ? <span className="cm-sub">save first</span> : <Attachments lead={l} onChange={onChange} compact />}</td>;
                  const expiring = key === "termExpires" && l.termExpires && l.termExpires >= today && l.termExpires <= new Date(Date.now() + 7 * 864e5).toISOString().slice(0, 10);
                  return (
                    <td key={i} className={expiring ? "tsg-warn" : ""}>
                      <Cell row={r} lead={l} disabled={isDraft && key !== "lender"} onCommit={(k, v) => (isDraft ? draftSet(l, k, v) : patchLead(l, k, v))} />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="cm-empty" style={{ marginTop: 8 }}>Each column is one lender. Type a company name in an empty column to create it, then fill the terms — every cell saves on blur. Highlighted columns have a live term sheet; leverage, debt service and DSCR compute from the deal's EBITDA and cash flow.</div>
    </Card>
  );
};
